import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import { GetAllCompanyDto } from "@/types/api/api-types";
import { useDeleteBillingEntity, usePatchBillingEntity } from "@/api/billingEntity.api";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { BillingEntityForm } from "@/components/shared/billingEntity/BillingEntityForm";
import { BillingEntityElem } from "./billingEntityElem";
import {
  ProfileBillingEntityFormData,
  profileBillingEntitySchema,
  DEFAULT_PROFILE_BILLING_ENTITY_VALUES,
} from "@/types/forms/shared/billingEntity.form";

type GeneralInfoModalCompanyProps = {
  closeModal: () => void;
  company: GetAllCompanyDto;
};

export const GeneralInfoModalCompany = ({ closeModal, company }: GeneralInfoModalCompanyProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const [selectedBillingEntity, setSelectedBillingEntity] = useState<string | null>(null);
  const { mutateAsync: patchBillingEntity, isPending } = usePatchBillingEntity();
  const { mutateAsync: deleteBillingEntity } = useDeleteBillingEntity();

  const billingEntityForm = useForm<ProfileBillingEntityFormData>({
    resolver: zodResolver(profileBillingEntitySchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_PROFILE_BILLING_ENTITY_VALUES,
  });

  const selectedBillingEntityData = company?.billingEntities.find(
    (el) => el.id === selectedBillingEntity,
  );

  const selectedBillingEntityName = selectedBillingEntityData?.companyName;
  const selectedBillingEntityIsNotDefault = !selectedBillingEntityData?.isDefault;

  const onSubmit: SubmitHandler<ProfileBillingEntityFormData> = async (data) => {
    if (!selectedBillingEntity) return;
    const ret = await patchBillingEntity({
      ...data,
      id: selectedBillingEntity,
    });
    if (ret?.status === 200) {
      endUpdate();
    }
  };

  const handleDeleteBillingEntity = (id: string) => {
    deleteBillingEntity(id);
  };
  const handleUpdateBillingEntity = (id: string) => {
    const billingEntity = company!.billingEntities.find((el) => el.id === id);
    if (!billingEntity) return;
    billingEntityForm.setValue("companyName", billingEntity.companyName);
    billingEntityForm.setValue("street", billingEntity.street);
    billingEntityForm.setValue("postalCode", billingEntity.postalCode);
    billingEntityForm.setValue("city", billingEntity.city);
    billingEntityForm.setValue("country", billingEntity.country);
    billingEntityForm.setValue("numberTVA", billingEntity.numberTVA ?? "");
    billingEntityForm.setValue("companySiret", billingEntity.companySiret);
    billingEntityForm.setValue("companyEori", billingEntity.companyEori ?? "");
    billingEntityForm.setValue("noEori", !billingEntity.companyEori);
    billingEntityForm.setValue("email", billingEntity.email);
    billingEntityForm.setValue("emailBis", billingEntity.emailBis ?? "");
    billingEntityForm.setValue("isDefault", billingEntity.isDefault);
    setSelectedBillingEntity(id);
  };

  const endUpdate = () => {
    setSelectedBillingEntity(null);
    billingEntityForm.reset();
  };

  // List view
  if (!selectedBillingEntity) {
    return (
      <Modal
        open={true}
        onClose={closeModal}
        title={t("profileBillingEntityModal.listTitle")}
        size="md"
        dismissible
      >
        <div className="flex w-full flex-col gap-4">
          <h3 className="text-brand700 text-base font-semibold">
            {t("profileBillingEntityModal.listSubtitle")}
          </h3>
          {company.billingEntities.map((billingEntity, index) => (
            <BillingEntityElem
              key={index}
              isDefault={billingEntity.isDefault}
              name={billingEntity.companyName}
              deleteBillingEntity={() => handleDeleteBillingEntity(billingEntity.id)}
              updateBillingEntity={() => handleUpdateBillingEntity(billingEntity.id)}
            />
          ))}
        </div>
      </Modal>
    );
  }

  // Edit view
  return (
    <Modal
      open={true}
      onClose={closeModal}
      title={t("profileBillingEntityModal.editTitle", { name: selectedBillingEntityName })}
      size="md"
      footer={
        <div className="flex w-full gap-2">
          <Button type="button" variant="secondary" onClick={endUpdate} className="flex-1">
            {t("common:actions.back")}
          </Button>
          <Button
            type="submit"
            form="edit-billing-entity-form"
            className="flex-1"
            disabled={isPending}
          >
            {t("profileBillingEntityModal.submit")}
          </Button>
        </div>
      }
    >
      <form
        id="edit-billing-entity-form"
        onSubmit={billingEntityForm.handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-3"
      >
        <BillingEntityForm
          control={billingEntityForm.control}
          showIsDefault={selectedBillingEntityIsNotDefault}
          setValue={billingEntityForm.setValue}
          isEditing={true}
        />
      </form>
    </Modal>
  );
};
